import React, { useEffect, useRef, useState } from "react";
import { useEditorStore } from "../../stores/editorStore";
import { useAuthStore } from "../../stores/authStore";
import { useNavigate, useParams } from "react-router-dom";
import { Globe } from "lucide-react";

interface PreviewPanelProps {
  isLoading?: boolean;
  loadingStep?: string;
  onSave?: () => void;
  onReset?: () => void;
  siteConfig?: any;
  page?: string;
  isComponentPanelFixed?: boolean;
  isPropertyPanelFixed?: boolean;
}

export default function PreviewPanel({
  isLoading = false,
  loadingStep = "",
  onSave,
  onReset,
  siteConfig,
  page = "home",
  isComponentPanelFixed = false,
  isPropertyPanelFixed = false,
}: PreviewPanelProps) {
  const navigate = useNavigate();
  const { siteId } = useParams<{ siteId: string }>();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const previewBoxRef = useRef<HTMLDivElement>(null);
  const [isStorefrontReady, setIsStorefrontReady] = useState(false);
  const { previewMode, setPreviewWidth } = useEditorStore();
  const { previewVersion } = useEditorStore();
  const { useDevBranch } = useAuthStore();
  const initializedRef = useRef(false);
  
  // Estado para o input de URL
  const [urlPath, setUrlPath] = useState(page === "home" ? "" : page);

  // Sincronizar input com a prop page
  useEffect(() => {
    setUrlPath(page === "home" ? "" : page);
  }, [page]);
  
  // Função helper para obter useDevBranch do localStorage diretamente
  const getUseDevBranchFromStorage = (): boolean => {
    try {
      const authStorage = localStorage.getItem('auth-storage');
      if (authStorage) {
        const parsed = JSON.parse(authStorage);
        return parsed?.state?.useDevBranch === true;
      }
    } catch (e) {
      console.error('Erro ao ler useDevBranch do localStorage:', e);
    }
    return false;
  };
  
  // Debug: log quando componente renderizar
  const currentUseDevBranch = getUseDevBranchFromStorage();
  console.log(`🔍 PreviewPanel renderizado - useDevBranch (store): ${useDevBranch}, useDevBranch (storage): ${currentUseDevBranch}, previewVersion: ${previewVersion}`);
  
  // Debug: verificar localStorage também
  useEffect(() => {
    const authStorage = localStorage.getItem('auth-storage');
    if (authStorage) {
      try {
        const parsed = JSON.parse(authStorage);
        console.log(`🔍 PreviewPanel - localStorage auth-storage:`, {
          useDevBranch: parsed?.state?.useDevBranch,
          storeUseDevBranch: useDevBranch
        });
      } catch (e) {
        console.error('Erro ao parsear auth-storage:', e);
      }
    }
  }, []);
  
  // Navegar para nova URL
  const handleUrlSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!siteId) return;

    // Normalizar path
    let newPath = urlPath.trim();
    if (!newPath.startsWith('/')) newPath = '/' + newPath;
    
    // Extrair page key (remove primeira barra)
    const pageKey = newPath === '/' ? 'home' : newPath.substring(1);
    
    // Navegar (isso vai triggerar o reload no EditorPage)
    navigate(`/editor/${siteId}/${pageKey}`);
  };

  // Debug: log quando useDevBranch mudar
  useEffect(() => {
    console.log(`🔍 PreviewPanel - useDevBranch mudou para: ${useDevBranch}`);
  }, [useDevBranch]);

  // A/B iframe state for seamless preview transitions
  const [urlA, setUrlA] = useState('');
  const [urlB, setUrlB] = useState('');
  const [activeIframe, setActiveIframe] = useState<'A' | 'B'>('A');
  const [isLoadingIframe, setIsLoadingIframe] = useState(false);

  // Preview via gateway: https://<host>/preview/<storeName>/
  const host = (import.meta as any).env.VITE_API_DOMAIN_HOST;
  let storeNameFromAuth = '';
  try {
    const authRaw = (typeof window !== 'undefined') ? localStorage.getItem('auth-storage') : null;
    if (authRaw) {
      const parsed = JSON.parse(authRaw);
      storeNameFromAuth = parsed?.state?.user?.storeName || '';
    }
  } catch {}

  const [previewBaseUrl, setPreviewBaseUrl] = useState(() => {
    try {
      const raw = localStorage.getItem('site-config');
      if (raw) return JSON.parse(raw)?.previewBaseUrl || '';
    } catch {}
    return '';
  });

  // Re-ler previewBaseUrl sempre que previewVersion mudar (load atualiza ambos)
  useEffect(() => {
    try {
      const raw = localStorage.getItem('site-config');
      if (raw) {
        const url = JSON.parse(raw)?.previewBaseUrl || '';
        if (url && url !== previewBaseUrl) {
          setPreviewBaseUrl(url);
        }
      }
    } catch {}
  }, [previewVersion]);
  
  const [livePreviewUrl, setLivePreviewUrl] = useState("");

  // Atualizar preview: delay de 8s somente no primeiro load; depois, imediato
  useEffect(() => {
    const normalizedPreviewBase = previewBaseUrl ? previewBaseUrl.replace(/\/+$/, '') : '';
    let baseUrl = '';

    if (normalizedPreviewBase) {
      const hasPreviewSegment = /\/preview(\/|$)/i.test(normalizedPreviewBase);
      const hasStoreInPreviewPath = /\/preview\/[^/]+$/i.test(normalizedPreviewBase);

      if (hasPreviewSegment && !hasStoreInPreviewPath && storeNameFromAuth) {
        // Shared VM expects /preview/<storeName>/...
        baseUrl = `${normalizedPreviewBase}/${encodeURIComponent(storeNameFromAuth)}/`;
      } else {
        baseUrl = `${normalizedPreviewBase}/`;
      }
    }

    if (!baseUrl) {
      console.log('🔍 PreviewPanel - previewBaseUrl ainda não definido, aguardando /render/load...');
      return;
    }

    const finalUseDevBranch = getUseDevBranchFromStorage() || useDevBranch;

    console.log(`🔍 PreviewPanel useEffect executado - useDevBranch (store): ${useDevBranch}, useDevBranch (storage): ${getUseDevBranchFromStorage()}, finalUseDevBranch: ${finalUseDevBranch}, previewVersion: ${previewVersion}, baseUrl: ${baseUrl}`);
    
    // Construir URL correta
    const pagePath = page === 'home' ? '' : page;
    // Evitar barra dupla se baseUrl termina com / e pagePath começa (embora pagePath aqui não comece com /)
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
    const fullUrl = cleanBaseUrl + pagePath;

    const joiner = fullUrl.includes('?') ? '&' : '?';
    const devParam = finalUseDevBranch ? '&dev=true' : '';
    const pageForQuery = page === 'home' ? 'home' : page;
    const encodedPage = encodeURIComponent(pageForQuery).replace(/%2F/g, '/');
    const pageParam = `&se_page=${encodedPage}`;
    const newLivePreviewUrl = `${fullUrl}${joiner}v=${previewVersion}${devParam}${pageParam}`;
    
    console.log(`🔍 PreviewPanel - URL construída: ${newLivePreviewUrl}, devParam: "${devParam}", finalUseDevBranch: ${finalUseDevBranch}`);
    
    if (!devParam && finalUseDevBranch) {
      console.error('❌ ERRO: devParam está vazio mas finalUseDevBranch é true!');
    }

    if (!isStorefrontReady) {
      const timer = setTimeout(() => {
        setIsStorefrontReady(true);
        setUrlA(newLivePreviewUrl); // Initial load into iframe A
        setActiveIframe('A');
      }, 3000);
      return () => clearTimeout(timer);
    } else {
      // Subsequent updates
      const currentActiveUrl = activeIframe === 'A' ? urlA : urlB;
      if (newLivePreviewUrl !== currentActiveUrl) {
        setIsLoadingIframe(true);
        if (activeIframe === 'A') {
          setUrlB(newLivePreviewUrl); // Load into inactive iframe B
        } else {
          setUrlA(newLivePreviewUrl); // Load into inactive iframe A
        }
      }
    }
  }, [previewBaseUrl, previewVersion, useDevBranch, isStorefrontReady, activeIframe, urlA, urlB, page]);

  // Observar largura do preview box (wrapper do iframe)
  useEffect(() => {
    const el = previewBoxRef.current;
    if (!el) return;

    const updateWidth = () => {
      const rect = el.getBoundingClientRect();
      setPreviewWidth(Math.round(rect.width));
    };

    updateWidth();

    const ro = new ResizeObserver(() => updateWidth());
    ro.observe(el);

    // Atualizar ao redimensionar janela
    const onWinResize = () => updateWidth();
    window.addEventListener('resize', onWinResize);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onWinResize);
      setPreviewWidth(null);
    };
  }, [setPreviewWidth]);

  // Re-medida imediata ao alternar entre desktop/mobile
  useEffect(() => {
    // aguarda aplicação das classes e layout
    const id = requestAnimationFrame(() => {
      const el = previewBoxRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        setPreviewWidth(Math.round(rect.width));
      }
    });
    return () => cancelAnimationFrame(id);
  }, [previewMode, setPreviewWidth]);

  const applyIframePatches = (iframeElement: HTMLIFrameElement, targetUrl: string) => {
    try {
      const win = iframeElement.contentWindow as any;
      const doc = iframeElement.contentDocument as Document | null;
      if (!win || !doc) return;

      const url = new URL(targetUrl);
      const version = url.searchParams.get('v') || '';

      // Helper to rewrite inline style URLs e preencher ?v= vazio (mantido, pois é seguro)
      const fixElementStyle = (el: Element) => {
        const styleAttr = el.getAttribute('style');
        if (!styleAttr) return;
        let next = styleAttr;
        next = next.replace(/url\((['"])\/localstatic\//g, `url($1/localstatic/`);
        next = next.replace(/url\(\/localstatic\//g, `url(/localstatic/`);
        if (version) {
          next = next.replace(/([?&]v=)(?=&|\)|$)/g, `$1${version}`);
        }
        if (next !== styleAttr) {
          el.setAttribute('style', next);
        }
      };

      try {
        const candidates = doc.querySelectorAll('[style*="/localstatic/"], [style*="v="]');
        candidates.forEach(fixElementStyle);
      } catch {}

      try {
        const mo = new MutationObserver((mutations) => {
          for (const m of mutations) {
            if (m.type === 'attributes' && m.attributeName === 'style' && m.target instanceof Element) {
              fixElementStyle(m.target);
            }
            if (m.type === 'childList') {
              m.addedNodes.forEach((n) => {
                if (n instanceof Element) {
                  fixElementStyle(n);
                  n.querySelectorAll('[style*="/localstatic/"], [style*="v="]').forEach(fixElementStyle);
                }
              });
            }
          }
        });
        mo.observe(doc.documentElement, { attributes: true, attributeFilter: ['style'], subtree: true, childList: true });
      } catch {}
    } catch {}
  };

  const handleIframeLoad = (event: React.SyntheticEvent<HTMLIFrameElement, Event>, iframeId: 'A' | 'B') => {
    const newUrl = iframeId === 'A' ? urlA : urlB;

    // Sempre aplicamos os patches (fetch / XHR / inline styles) em qualquer load,
    // independentemente do estado de double-buffering, para garantir que
    // chamadas diretas a 127.0.0.1:5501/5502 (ex: /snippet) sejam reescritas.
    applyIframePatches(event.currentTarget, newUrl);

    // Mantém a lógica de double-buffering apenas para controlar qual iframe é o ativo
    if (isLoadingIframe && iframeId !== activeIframe) {
      setActiveIframe(iframeId);
      setIsLoadingIframe(false);
    }
  };

  const showLoading = isLoading || isLoadingIframe;
  
  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Barra de Endereço */}
      <div className="ml-8 px-2 py-2 flex items-center space-x-2">
        <form onSubmit={handleUrlSubmit} className="flex-1 flex items-center bg-gray-100 rounded-md px-3 py-1.5 focus-within:ring-2 focus-within:ring-primary-500 transition-all">
          <Globe className="w-4 h-4 text-gray-400 mr-2" />
          <span className="text-gray-400 text-sm mr-1">/</span>
          <input 
            type="text" 
            value={urlPath}
            onChange={(e) => setUrlPath(e.target.value)}
            className="flex-1 bg-transparent border-none outline-none text-sm text-gray-700 placeholder-gray-400"
            placeholder="digite a url da página desejada"
          />
        </form>
      </div>

      {/* Área do Preview */}
      <div className="flex-1 bg-gray-100 p-2 ml-8 overflow-hidden">
        <div className="h-full flex justify-center" ref={containerRef}>
          <div
            ref={previewBoxRef}
            className={`bg-white rounded-md shadow-sm overflow-hidden relative ${
              previewMode === 'mobile' 
                ? 'w-[375px] h-[667px] rounded-3xl mobile-preview' 
                : 'w-full h-full'
            }`}
          >
            {!isStorefrontReady ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                  <p className="text-gray-600">Aguardando storefront inicializar...</p>
                  <p className="text-sm text-gray-400 mt-2">Isso pode levar alguns segundos na primeira vez</p>
                </div>
              </div>
            ) : (
              <>
                <iframe
                  title="Preview A"
                  src={urlA}
                  onLoad={(e) => handleIframeLoad(e, 'A')}
                  className="w-full h-full border-0 absolute top-0 left-0"
                  style={{
                    opacity: activeIframe === 'A' ? 1 : 0,
                    pointerEvents: activeIframe === 'A' ? 'auto' : 'none',
                  }}
                />
                <iframe
                  title="Preview B"
                  src={urlB}
                  onLoad={(e) => handleIframeLoad(e, 'B')}
                  className="w-full h-full border-0 absolute top-0 left-0"
                  style={{
                    opacity: activeIframe === 'B' ? 1 : 0,
                    pointerEvents: activeIframe === 'B' ? 'auto' : 'none',
                  }}
                />
                {isLoadingIframe && (
                  <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                      <p className="text-gray-600 font-medium">Atualizando preview...</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
